/**
 * QultureUp difficulty — a mission's level and a knowledge question's tier, in
 * the reader's language, on one scale. (MOB-27)
 *
 * The phone's assessment printed the question's raw tier ("Medium") in a
 * traffic-light status colour (red for "hard"), a mission's hero badge printed
 * its raw level ("advanced"), the world map drew its English initial, and the
 * web's Brain assessment widget printed the raw tier too. Difficulty is not a
 * status: every surface draws it through these labels and places a question
 * tier on the mission scale, so both share the one difficulty palette.
 */

export const QULTUREUP_MISSION_DIFFICULTIES = ['beginner', 'intermediate', 'advanced'] as const;
export type QultureUpMissionDifficulty = (typeof QULTUREUP_MISSION_DIFFICULTIES)[number];

export const QULTUREUP_MISSION_DIFFICULTY_LABEL_KEY = {
  beginner: 'qultureup.difficultyBeginner',
  intermediate: 'qultureup.difficultyIntermediate',
  advanced: 'qultureup.difficultyAdvanced',
} as const satisfies Record<QultureUpMissionDifficulty, string>;

/** How hard a question's mission is — the tier the question pool is drawn by. */
export const QULTUREUP_DIFFICULTY_TIERS = ['easy', 'medium', 'hard'] as const;
export type QultureUpDifficultyTier = (typeof QULTUREUP_DIFFICULTY_TIERS)[number];

export const QULTUREUP_DIFFICULTY_TIER_LABEL_KEY = {
  easy: 'qultureup.questionTier.easy',
  medium: 'qultureup.questionTier.medium',
  hard: 'qultureup.questionTier.hard',
} as const satisfies Record<QultureUpDifficultyTier, string>;

/** Where a question tier sits on the mission scale — so both draw one palette. */
export const QULTUREUP_TIER_ON_MISSION_SCALE = {
  easy: 'beginner',
  medium: 'intermediate',
  hard: 'advanced',
} as const satisfies Record<QultureUpDifficultyTier, QultureUpMissionDifficulty>;

/** A level's step on the scale, 1–3: a language-free mark where a label cannot fit. */
export function qultureUpDifficultyStep(level: QultureUpMissionDifficulty): 1 | 2 | 3 {
  switch (level) {
    case 'beginner':
      return 1;
    case 'intermediate':
      return 2;
    case 'advanced':
      return 3;
    default: {
      const unhandled: never = level;
      return unhandled;
    }
  }
}

export function isQultureUpMissionDifficulty(value: unknown): value is QultureUpMissionDifficulty {
  return (
    typeof value === 'string' &&
    (QULTUREUP_MISSION_DIFFICULTIES as readonly string[]).includes(value)
  );
}
